import {
  CreateOfferRequest,
  CreateOfferRequestSchema,
  GetOffersQuery,
  GetOffersResponse,
  GetOffersResponseSchema,
  OfferDetailResponse,
  OfferDetailResponseSchema,
  UpdateOfferRequest,
  UpdateOfferRequestSchema,
} from "@codway-dev/shared";
import { AuthService } from "./auth.service";

const API_BASE_URL = "/api";

async function authHeaders(): Promise<HeadersInit> {
  const token = await AuthService.getShopifyToken();
  if (!token) throw new Error("No Shopify token available");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

function buildListQuery(query: Partial<GetOffersQuery> = {}): string {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.set("page", String(query.page));
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.search) params.set("search", query.search);
  if (query.is_active !== undefined)
    params.set("is_active", query.is_active ? "true" : "false");
  if (query.display_type) params.set("display_type", query.display_type);
  if (query.placement) params.set("placement", query.placement);
  if (query.kind) params.set("kind", query.kind);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const OfferService = {
  list: async (
    query: Partial<GetOffersQuery> = {}
  ): Promise<GetOffersResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/admin/offers${buildListQuery(query)}`,
      { method: "GET", headers: await authHeaders() }
    );
    if (!response.ok) {
      throw new Error(`Failed to load offers: ${response.statusText}`);
    }
    const data = await response.json();
    return GetOffersResponseSchema.parse(data);
  },

  get: async (id: string): Promise<OfferDetailResponse> => {
    const response = await fetch(`${API_BASE_URL}/admin/offers/${id}`, {
      method: "GET",
      headers: await authHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to load offer: ${response.statusText}`);
    }
    const data = await response.json();
    return OfferDetailResponseSchema.parse(data);
  },

  create: async (body: CreateOfferRequest): Promise<OfferDetailResponse> => {
    const valid = CreateOfferRequestSchema.parse(body);
    const response = await fetch(`${API_BASE_URL}/admin/offers`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify(valid),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        `Failed to create offer: ${err.message || response.statusText}`
      );
    }
    const data = await response.json();
    return OfferDetailResponseSchema.parse(data);
  },

  update: async (
    id: string,
    body: UpdateOfferRequest
  ): Promise<OfferDetailResponse> => {
    const valid = UpdateOfferRequestSchema.parse(body);
    const response = await fetch(`${API_BASE_URL}/admin/offers/${id}`, {
      method: "PUT",
      headers: await authHeaders(),
      body: JSON.stringify(valid),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        `Failed to update offer: ${err.message || response.statusText}`
      );
    }
    const data = await response.json();
    return OfferDetailResponseSchema.parse(data);
  },

  remove: async (id: string): Promise<void> => {
    const token = await AuthService.getShopifyToken();
    if (!token) throw new Error("No Shopify token available");
    // Intentionally NOT using authHeaders() — that helper sets
    // Content-Type: application/json, but DELETE has no body. Fastify's
    // content-type parser rejects bodyless requests with a JSON content-type.
    const response = await fetch(`${API_BASE_URL}/admin/offers/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new Error(`Failed to delete offer: ${response.statusText}`);
    }
  },
};
